import React, { useEffect, useMemo } from 'react';
import DocumentActionsSection from 'components/common/documents-upload/DocumentActionsSection';
import PageHeader from 'components/common/general/layouts/PageHeader';
import { observer } from 'mobx-react-lite';
import PageFooter from 'components/common/general/layouts/PageFooter';
import { DocumentList } from 'components/common/documents-upload/DocumentsList';

import { useHistory } from 'react-router-dom';

import UploadInitiateRFQDocumentsPageStore, {
  UploadInitiateRFQDocumentsPageStoreContext,
  useUploadInitiateRFQDocumentsPageStore,
} from 'store/pages/rfq/initiate-rfq/upload-initiate-rfq-documents/pageStore';
import {
  RFQ_PROPOSAL_CARD_DATA,
  getRFQProposalChecklistURL,
} from 'global/checklists-helpers/RFQProposalChecklistHelper';
import RFQStatusLayout from 'components/common/rfq/RFQStatusLayout';
import useRfqQuoteUrlParams from 'global/hooks/useRfqQuoteUrlParams';
import { InfoSection } from 'components/common/documents-upload/InfoSection';

const PageContent: React.FC = observer(() => {
  const pageStore = useUploadInitiateRFQDocumentsPageStore();
  const { periodId, cartId } = useRfqQuoteUrlParams();

  const history = useHistory();

  useEffect(() => {
    void pageStore.loadInitialData();
  }, []);

  const onBack = (): void => {
    const checklistURL = getRFQProposalChecklistURL({ periodId, cartId });
    history.push(checklistURL);
  };

  const onStepComplete = async (): Promise<void> => {
    await pageStore.completeStep();
    onBack();
  };

  return (
    <>
      <PageHeader
        title={RFQ_PROPOSAL_CARD_DATA.INITIATE_RFQ_DOCUMENTS.title}
        subtitle={RFQ_PROPOSAL_CARD_DATA.INITIATE_RFQ_DOCUMENTS.text}
        subtitleMaxWidth="51rem"
        onRedirect={onBack}
      />
      {pageStore.activeDocuments.length > 0 && <InfoSection />}
      <DocumentActionsSection
        requestDocumentModalStore={pageStore.requestDocumentModalStore}
        uploadDocumentModalStore={pageStore.uploadDocumentModalStore}
        loading={pageStore.isPageLoading}
        disableMutativeActions={false}
      />
      <DocumentList
        activeDocuments={pageStore.activeDocuments}
        inactiveDocuments={pageStore.inactiveDocuments}
        loading={pageStore.isPageLoading}
      />
      <PageFooter
        disableCompleteButton={pageStore.isPageLoading || !pageStore.pageDomainCompletion.allowed}
        completeDisabledReason={pageStore.pageDomainCompletion.notAllowedMessage}
        onComplete={onStepComplete}
        onBack={onBack}
      />
    </>
  );
});

const InitiateRFQDocumentsPage: React.FC = () => {
  const { periodId, cartId } = useRfqQuoteUrlParams();

  const pageStore = useMemo(
    () => new UploadInitiateRFQDocumentsPageStore({ affiliatedId: periodId, entityId: cartId }),
    [],
  );

  return (
    <RFQStatusLayout>
      <UploadInitiateRFQDocumentsPageStoreContext.Provider value={pageStore}>
        <PageContent />
      </UploadInitiateRFQDocumentsPageStoreContext.Provider>
    </RFQStatusLayout>
  );
};

export default InitiateRFQDocumentsPage;
